/**
 * The two ways of looking at a folder's contents.
 *
 * A list is right for mixed content - you are reading names, sizes and dates.
 * A grid is right for photos, where the picture is the identifier. Which one
 * opens by default is decided per folder by what is actually in it.
 */
import { Link } from '@tanstack/react-router';
import { MoreVertical } from 'lucide-react';
import { formatBytes, formatDate } from '../lib/format';
import { Checkbox, DropdownMenu, Pill, cx } from './ui';
import { FolderIcon, Thumb } from './Thumb';
import type { EntryDto, FileDto } from '../api/types';
import type { ReactNode } from 'react';

export interface EntryViewProps {
  entries: EntryDto[];
  /**
   * Asked per entry rather than given a Set of ids, because a selection can
   * now span pages - "select all matching" holds ids that are not on screen -
   * and files and folders are tracked separately so an id alone is ambiguous.
   */
  isSelected: (entry: EntryDto) => boolean;
  onToggle: (entry: EntryDto) => void;
  selectionMode: boolean;
  onOpenFile: (file: FileDto) => void;
  /** Rendered inside each row's overflow menu. */
  menuFor: (entry: EntryDto) => ReactNode;
  /** Shown on shared items so it is obvious whose file it is. */
  showOwner?: boolean;
}

function folderLink(id: string) {
  return { to: '/folders/$folderId', params: { folderId: id } } as const;
}

export function EntryList(props: EntryViewProps) {
  const { entries, isSelected: selectedFor, onToggle, selectionMode, onOpenFile, menuFor, showOwner } = props;

  return (
    <ul className="divide-y divide-line">
      {entries.map((entry) => {
        const isSelected = selectedFor(entry);
        return (
          <li
            key={entry.id}
            className={cx(
              'flex items-center gap-3 px-3 py-2.5 sm:px-4',
              isSelected ? 'bg-brand/8' : 'hover:bg-surface-2',
            )}
          >
            {selectionMode ? (
              <Checkbox checked={isSelected} onCheckedChange={() => onToggle(entry)} />
            ) : null}

            <div className="size-10 shrink-0">
              {entry.kind === 'folder' ? (
                <div className="flex size-10 items-center justify-center rounded-lg bg-surface-2">
                  <FolderIcon />
                </div>
              ) : (
                <Thumb file={entry} size={96} className="size-10" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              {entry.kind === 'folder' ? (
                <Link {...folderLink(entry.id)} className="block truncate font-medium hover:underline">
                  {entry.name}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenFile(entry)}
                  className="block max-w-full truncate text-left font-medium hover:underline"
                >
                  {entry.name}
                </button>
              )}
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 truncate text-xs text-ink-dim">
                <span>
                  {entry.kind === 'folder'
                    ? `${entry.fileCount} file${entry.fileCount === 1 ? '' : 's'}`
                    : formatBytes(entry.sizeBytes)}
                </span>
                <span aria-hidden>·</span>
                <span>{formatDate(entry.kind === 'file' ? (entry.takenAt ?? entry.createdAt) : entry.createdAt)}</span>
                {showOwner && entry.role !== 'owner' ? (
                  <>
                    <span aria-hidden>·</span>
                    <span className="truncate">{entry.ownerName}</span>
                  </>
                ) : null}
                {entry.description ? (
                  <span className="hidden truncate text-ink-dim/80 sm:inline">
                    — {entry.description}
                  </span>
                ) : null}
              </p>
            </div>

            {entry.role === 'viewer' ? <Pill>View only</Pill> : null}

            <DropdownMenu
              ariaLabel={`Actions for ${entry.name}`}
              trigger={
                <span className="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg text-ink-dim hover:bg-surface-2">
                  <MoreVertical className="size-5" />
                </span>
              }
            >
              {menuFor(entry)}
            </DropdownMenu>
          </li>
        );
      })}
    </ul>
  );
}

export function EntryGrid(props: EntryViewProps) {
  const { entries, isSelected: selectedFor, onToggle, selectionMode, onOpenFile, menuFor } = props;

  return (
    <ul className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-3 sm:gap-4 sm:p-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
      {entries.map((entry) => {
        const isSelected = selectedFor(entry);
        return (
          <li key={entry.id} className="group relative">
            <div
              className={cx(
                'overflow-hidden rounded-xl border transition-colors',
                isSelected ? 'border-brand ring-2 ring-brand/40' : 'border-line',
              )}
            >
              {entry.kind === 'folder' ? (
                <Link
                  {...folderLink(entry.id)}
                  className="flex aspect-square items-center justify-center bg-surface-2"
                >
                  <FolderIcon className="size-10" />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => (selectionMode ? onToggle(entry) : onOpenFile(entry))}
                  className="block w-full"
                  aria-label={entry.name}
                >
                  <Thumb file={entry} size={480} rounded={false} className="aspect-square w-full" />
                </button>
              )}
              <div className="flex items-center gap-1.5 bg-surface px-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{entry.name}</p>
                  <p className="truncate text-xs text-ink-dim">
                    {entry.kind === 'folder'
                      ? `${entry.fileCount} file${entry.fileCount === 1 ? '' : 's'}`
                      : formatBytes(entry.sizeBytes)}
                  </p>
                </div>
                <DropdownMenu
                  ariaLabel={`Actions for ${entry.name}`}
                  trigger={
                    <span className="inline-flex size-8 cursor-pointer items-center justify-center rounded-lg text-ink-dim hover:bg-surface-2">
                      <MoreVertical className="size-4" />
                    </span>
                  }
                >
                  {menuFor(entry)}
                </DropdownMenu>
              </div>
            </div>

            {selectionMode ? (
              <div className="absolute top-2 left-2 rounded-md bg-surface/90 p-1 backdrop-blur">
                <Checkbox checked={isSelected} onCheckedChange={() => onToggle(entry)} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
